import createUseStyles from '@picsart/rc/create';
import { BreakPoints } from '@picsart/rc/enums';
import { mediaQuery } from '@picsart/rc/helpers';

function generateColumnSize(
  columnsCount: number,
  singleColumnSize: number,
): string {
  return `${(100 / columnsCount) * singleColumnSize}%`;
}

function getWidth(width: number | string, paddings: number): string {
  if (width) {
    if (width.toString().endsWith('%')) {
      return String(width);
    }
    if (paddings) {
      return `${+width + paddings}px`;
    }
    return `${width}px`;
  }

  return '';
}

const calculateWidth =
  (prefix: string) =>
  (props: any): string =>
    props.isDynamic
      ? generateColumnSize(
          props[`${prefix}_columnsCount`],
          props[`${prefix}_columnSize`],
        )
      : getWidth(
          props[`${prefix}_columnWidth`],
          props[`${prefix}_gridHorizontalGutter`],
        );

const calculatePadding = (prefix: string) => (props: any) => {
  const verticalGutter = props[`${prefix}_gridVerticalGutter`] / 2;
  const horizontalGutter = props[`${prefix}_gridHorizontalGutter`] / 2;
  return `${verticalGutter}px ${horizontalGutter}px`;
};

const calculateMargin = (prefix: string) => props => {
  const verticalGutter = props[`${prefix}_gridVerticalGutter`];
  const horizontalGutter = props[`${prefix}_gridHorizontalGutter`];
  return `-${verticalGutter}px -${horizontalGutter}px`;
};

const useMasonryGridVerticalStyles = createUseStyles(
  {
    root: {
      contain: 'layout',
      contentVisibility: 'auto',
      containIntrinsicSize: '100vh',
      pointerEvents: 'none',
    },
    grid: {
      padding: calculatePadding('XL'),

      [mediaQuery(BreakPoints.ExtraLarge)]: {
        padding: calculatePadding('LG'),
      },
      [mediaQuery(BreakPoints.Large)]: {
        padding: calculatePadding('MD'),
      },
      [mediaQuery(BreakPoints.Medium)]: {
        padding: calculatePadding('SM'),
      },
    },
    row: {
      display: 'flex',
      flexWrap: 'wrap',
      flexDirection: (props: any) => !props.isSSR && 'column',
      margin: calculateMargin('XL'),
      [mediaQuery(BreakPoints.ExtraLarge)]: {
        margin: calculateMargin('LG'),
      },
      [mediaQuery(BreakPoints.Large)]: {
        margin: calculateMargin('MD'),
      },
      [mediaQuery(BreakPoints.Medium)]: {
        margin: calculateMargin('SM'),
      },
    },
    column: {
      minWidth: calculateWidth('XL'),
      maxWidth: calculateWidth('XL'),
      padding: calculatePadding('XL'),
      [mediaQuery(BreakPoints.ExtraLarge)]: {
        minWidth: calculateWidth('LG'),
        maxWidth: calculateWidth('LG'),
        padding: calculatePadding('LG'),
      },
      [mediaQuery(BreakPoints.Large)]: {
        minWidth: calculateWidth('MD'),
        maxWidth: calculateWidth('MD'),
        padding: calculatePadding('MD'),
      },
      [mediaQuery(BreakPoints.Medium)]: {
        minWidth: calculateWidth('SM'),
        maxWidth: calculateWidth('SM'),
        padding: calculatePadding('SM'),
      },
      '& > *': {
        pointerEvents: 'all',
      },
    },
  },
  { name: 'masonry-vertical' },
);

export default useMasonryGridVerticalStyles;
